import { CROP_TYPE_FCPO, FIELD_VERIFICATION_FILTER, FieldVerification, FieldVerificationFilter, ZERO_UUID } from "models";

/**
 * Parsed filter criteria for field practice filtering (crops, fertilizers, tillage, and seeding)
 */
export type FilterCriteria = {
  cropName?: string;
  cropType?: CROP_TYPE_FCPO;
  year?: number;
  fertilizerName?: string;
  fertilizerType?: "mineralFertilizers" | "organicFertilizers";
  tillageName?: string;
  seedingName?: string;
};

/**
 * Parses a filter string into structured criteria
 * @param filter - Filter string in format "crop-Name|year-2026|cropType-COVER" or "fertilizer-Name|year-2026" or "fertilizerType-mineralFertilizers|year-2026" or "tillage-Name|year-2026" or "seeding-Name|year-2026"
 * @returns Parsed filter criteria
 */
export const parseFilter = (filter: string): FilterCriteria => {
  const parts = filter.split("|");
  const criteria: FilterCriteria = {};

  parts.forEach((part) => {
    if (part.startsWith("crop-") && !part.startsWith("cropType-")) {
      const cropName = part.replace("crop-", "");
      if (cropName) {
        criteria.cropName = cropName;
      }
    } else if (part.startsWith("cropType-")) {
      const cropTypeValue = part.replace("cropType-", "");
      // Validate that it's a valid CROP_TYPE_FCPO enum value
      if (Object.values(CROP_TYPE_FCPO).includes(cropTypeValue as CROP_TYPE_FCPO)) {
        criteria.cropType = cropTypeValue as CROP_TYPE_FCPO;
      }
    } else if (part.startsWith("fertilizerType-")) {
      const fertilizerTypeValue = part.replace("fertilizerType-", "");
      // Validate that it's a valid fertilizer type
      if (fertilizerTypeValue === "mineralFertilizers" || fertilizerTypeValue === "organicFertilizers") {
        criteria.fertilizerType = fertilizerTypeValue as "mineralFertilizers" | "organicFertilizers";
      }
    } else if (part.startsWith("fertilizer-") && !part.startsWith("fertilizerType-")) {
      const fertilizerName = part.replace("fertilizer-", "");
      if (fertilizerName) {
        criteria.fertilizerName = fertilizerName;
      }
    } else if (part.startsWith("tillage-")) {
      const tillageName = part.replace("tillage-", "");
      if (tillageName) {
        criteria.tillageName = tillageName;
      }
    } else if (part.startsWith("seeding-")) {
      const seedingName = part.replace("seeding-", "");
      if (seedingName) {
        criteria.seedingName = seedingName;
      }
    } else if (part.startsWith("year-")) {
      const yearValue = parseInt(part.replace("year-", ""), 10);
      if (!Number.isNaN(yearValue) && yearValue > 0) {
        criteria.year = yearValue;
      }
    }
  });

  return criteria;
};

/**
 * Type for crop data stored in the field crops map
 */
export type FieldCropData = {
  cropName: string;
  year?: number;
  cropType: CROP_TYPE_FCPO;
};

/**
 * Type for fertilizer data stored in the field fertilizers map
 */
export type FieldFertilizerData = {
  fertilizerName: string;
  year?: number;
  type: "mineralFertilizers" | "organicFertilizers";
  assignedPracticeId: string; // Used to check if it's ZERO_UUID (no fertilizer applied)
};

/**
 * Type for tillage data stored in the field tillages map
 */
export type FieldTillageData = {
  practiceName: string;
  year?: number;
  type: "tillages";
  assignedPracticeId: string;
};

/**
 * Type for seeding data stored in the field seedings map
 */
export type FieldSeedingData = {
  practiceName: string;
  year?: number;
  type: "seedings";
  assignedPracticeId: string;
};

/**
 * Checks if a field matches the given filter criteria
 * @param field - Field to check
 * @param criteria - Filter criteria to match against
 * @param fieldCropsMap - Map of fieldId to crops data
 * @param fieldFertilizersMap - Map of fieldId to fertilizers data
 * @param fieldTillagesMap - Map of fieldId to tillages data
 * @param fieldSeedingsMap - Map of fieldId to seedings data
 * @returns True if field matches criteria
 */
export const matchesFilterCriteria = (
  field: FieldVerification,
  criteria: FilterCriteria,
  fieldCropsMap: Map<string, FieldCropData[]> | null,
  fieldFertilizersMap: Map<string, FieldFertilizerData[]> | null,
  fieldTillagesMap: Map<string, FieldTillageData[]> | null = null,
  fieldSeedingsMap: Map<string, FieldSeedingData[]> | null = null
): boolean => {
  // Handle tillage filtering
  if (criteria.tillageName !== undefined) {
    if (!fieldTillagesMap) return false;

    const fieldTillages = fieldTillagesMap.get(field.id) || [];

    // If tillageName is empty or invalid, don't filter
    if (!criteria.tillageName) {
      return true;
    }

    // Check if there's a single tillage that matches ALL criteria
    return fieldTillages.some((tillage) => {
      let matches = true;
      if (criteria.tillageName !== undefined && criteria.tillageName) {
        matches = matches && tillage.practiceName === criteria.tillageName;
      }
      if (criteria.year !== undefined) {
        matches = matches && tillage.year === criteria.year;
      }
      return matches;
    });
  }

  // Handle seeding filtering
  if (criteria.seedingName !== undefined) {
    if (!fieldSeedingsMap) return false;

    const fieldSeedings = fieldSeedingsMap.get(field.id) || [];

    // If seedingName is empty or invalid, don't filter
    if (!criteria.seedingName) {
      return true;
    }

    // Check if there's a single seeding that matches ALL criteria
    return fieldSeedings.some((seeding) => {
      let matches = true;
      if (criteria.seedingName !== undefined && criteria.seedingName) {
        matches = matches && seeding.practiceName === criteria.seedingName;
      }
      if (criteria.year !== undefined) {
        matches = matches && seeding.year === criteria.year;
      }
      return matches;
    });
  }
  // Handle fertilizer filtering
  if (criteria.fertilizerName !== undefined || criteria.fertilizerType !== undefined) {
    if (!fieldFertilizersMap) return false;

    const fieldFertilizers = fieldFertilizersMap.get(field.id) || [];

    // If fertilizerName is empty or invalid, and no fertilizerType, don't filter
    if (!criteria.fertilizerName && !criteria.fertilizerType) {
      return true;
    }

    // Check if there's a single fertilizer that matches ALL criteria
    // If only fertilizerName is set: show all fields with that fertilizer (any year)
    // If only fertilizerType is set: show all fields with that fertilizer type (any year)
    // If both fertilizerName/fertilizerType and year are set: show only fields with that fertilizer/type in that year
    // Exclude fertilizers with ZERO_UUID (no fertilizer applied) when filtering by fertilizer type
    return fieldFertilizers.some((fertilizer) => {
      // When filtering by fertilizer type, exclude "No fertilizer applied" entries (ZERO_UUID)
      if (criteria.fertilizerType !== undefined && fertilizer.assignedPracticeId === ZERO_UUID) {
        return false;
      }

      let matches = true;
      if (criteria.fertilizerName !== undefined && criteria.fertilizerName) {
        matches = matches && fertilizer.fertilizerName === criteria.fertilizerName;
      }
      if (criteria.fertilizerType !== undefined) {
        matches = matches && fertilizer.type === criteria.fertilizerType;
      }
      if (criteria.year !== undefined) {
        matches = matches && fertilizer.year === criteria.year;
      }
      return matches;
    });
  }

  // Handle crop filtering
  if (!fieldCropsMap) return false;

  const fieldCrops = fieldCropsMap.get(field.id) || [];

  // If no valid criteria were parsed, don't filter
  if (!criteria.cropName && !criteria.cropType && !criteria.year) {
    return true;
  }

  // Check if there's a single crop that matches ALL criteria
  return fieldCrops.some((crop) => {
    let matches = true;
    if (criteria.cropName !== undefined) {
      matches = matches && crop.cropName === criteria.cropName;
    }
    if (criteria.cropType !== undefined) {
      matches = matches && crop.cropType === criteria.cropType;
    }
    if (criteria.year !== undefined) {
      matches = matches && crop.year === criteria.year;
    }
    return matches;
  });
};

/**
 * Filters fields based on the selected filter
 * @param fields - Array of fields to filter
 * @param filter - Filter string or enum value
 * @param fieldCropsMap - Map of fieldId to crops data for crop-based filtering
 * @param fieldFertilizersMap - Map of fieldId to fertilizers data for fertilizer-based filtering
 * @param fieldTillagesMap - Map of fieldId to tillages data for tillage-based filtering
 * @param fieldSeedingsMap - Map of fieldId to seedings data for seeding-based filtering
 * @returns Filtered array of fields
 */
export const filterFields = (
  fields: FieldVerification[],
  filter: FieldVerificationFilter | undefined,
  fieldCropsMap: Map<string, FieldCropData[]> | null,
  fieldFertilizersMap: Map<string, FieldFertilizerData[]> | null = null,
  fieldTillagesMap: Map<string, FieldTillageData[]> | null = null,
  fieldSeedingsMap: Map<string, FieldSeedingData[]> | null = null
): FieldVerification[] => {
  if (!filter || filter === FIELD_VERIFICATION_FILTER.allFields) return fields;

  return fields.filter((field) => {
    switch (filter) {
      case FIELD_VERIFICATION_FILTER.pendingVerification:
        return field.verifications.some(({ verified }) => verified === null);

      case FIELD_VERIFICATION_FILTER.pendingCrops:
        return field.pendingPractices.crops > 0;

      case FIELD_VERIFICATION_FILTER.pendingFertilizers:
        return field.pendingPractices.fertilizers > 0;

      case FIELD_VERIFICATION_FILTER.pendingTillageSeeding:
        return field.pendingPractices.tillageSeeding > 0;

      // Editing request filters for crops (crop-level)
      case FIELD_VERIFICATION_FILTER.hasCropEditingRequests: {
        const { cropLevel } = field.editingRequests;
        return cropLevel.open + cropLevel.toReview + cropLevel.reviewed > 0;
      }

      case FIELD_VERIFICATION_FILTER.openEditingRequests:
        return field.editingRequests.cropLevel.open > 0;

      case FIELD_VERIFICATION_FILTER.toReviewEditingRequests:
        return field.editingRequests.cropLevel.toReview > 0;

      case FIELD_VERIFICATION_FILTER.reviewedEditingRequests:
        return field.editingRequests.cropLevel.reviewed > 0;

      // Editing request filters for fertilizers (fertilizer-level)
      case FIELD_VERIFICATION_FILTER.hasFertilizerEditingRequests: {
        const { fertilizerLevel } = field.editingRequests;
        return fertilizerLevel.open + fertilizerLevel.toReview + fertilizerLevel.reviewed > 0;
      }

      case FIELD_VERIFICATION_FILTER.openFertilizerEditingRequests:
        return field.editingRequests.fertilizerLevel.open > 0;

      case FIELD_VERIFICATION_FILTER.toReviewFertilizerEditingRequests:
        return field.editingRequests.fertilizerLevel.toReview > 0;

      case FIELD_VERIFICATION_FILTER.reviewedFertilizerEditingRequests:
        return field.editingRequests.fertilizerLevel.reviewed > 0;

      // Editing request filters for tillage & seeding (tillageSeeding-level)
      case FIELD_VERIFICATION_FILTER.hasTillageSeedingEditingRequests: {
        const { tillageSeedingLevel } = field.editingRequests;
        return tillageSeedingLevel.open + tillageSeedingLevel.toReview + tillageSeedingLevel.reviewed > 0;
      }

      case FIELD_VERIFICATION_FILTER.openTillageSeedingEditingRequests:
        return field.editingRequests.tillageSeedingLevel.open > 0;

      case FIELD_VERIFICATION_FILTER.toReviewTillageSeedingEditingRequests:
        return field.editingRequests.tillageSeedingLevel.toReview > 0;

      case FIELD_VERIFICATION_FILTER.reviewedTillageSeedingEditingRequests:
        return field.editingRequests.tillageSeedingLevel.reviewed > 0;

      default: {
        // Handle crop, year, crop type, fertilizer, tillage, and seeding filtering
        const criteria = parseFilter(filter);
        return matchesFilterCriteria(field, criteria, fieldCropsMap, fieldFertilizersMap, fieldTillagesMap, fieldSeedingsMap);
      }
    }
  });
};
